import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import styles from './index.module.less';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { CREATOR_POST_URLS, ERASA_X_URL } from '../../constants';

// 推广文案示例：设计稿 #10899:3769 / #10899:3778，段落间 gap 16
const POST_IDEAS = [
  [
    'Been looking into ways to deal with leaked content without having to chase links and file reports all day.',
    'Erasa scans for stolen content and handles the takedowns for you. They’ve also got a free leak scan + takedown trial if you want to see what’s already out there.',
    '[Your referral link]',
  ],
  [
    'If you’re tired of finding your videos or content reposted without permission and having to report every copy yourself, this might be useful.',
    'Erasa scans the web for stolen and leaked content and handles the takedowns. There’s a free scan + takedown trial too, so you can test it first.',
    '[Your referral link]',
  ],
];

// 16×16 单色图形，内联以支持 currentColor
const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M13.3334 1.33331H6.66671C5.93337 1.33331 5.33337 1.93331 5.33337 2.66665V9.33331C5.33337 10.0666 5.93337 10.6666 6.66671 10.6666H13.3334C14.0667 10.6666 14.6667 10.0666 14.6667 9.33331V2.66665C14.6667 1.93331 14.0667 1.33331 13.3334 1.33331ZM13.3334 9.33331H6.66671V2.66665H13.3334V9.33331Z" />
    <path d="M9.33337 13.3333H2.66671V6.66665H4.00004V5.33331H2.66671C1.93337 5.33331 1.33337 5.93331 1.33337 6.66665V13.3333C1.33337 14.0666 1.93337 14.6666 2.66671 14.6666H9.33337C10.0667 14.6666 10.6667 14.0666 10.6667 13.3333V12H9.33337V13.3333Z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// #10899:3796，14×14。原 SVG 带一个与 viewBox 等大的冗余 clipPath，内联时已去掉
const BadgeCheckIcon = () => (
  <svg className={styles.badge} width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M5.25024 6.99943L6.41691 8.1661L8.75024 5.83276M2.24619 5.02782C2.16105 4.64429 2.17412 4.24548 2.2842 3.86835C2.39428 3.49122 2.59779 3.148 2.87588 2.87049C3.15397 2.59299 3.49762 2.39019 3.87498 2.2809C4.25233 2.17161 4.65118 2.15937 5.03452 2.24532C5.24552 1.91533 5.53619 1.64376 5.87974 1.45565C6.2233 1.26754 6.60868 1.16895 7.00036 1.16895C7.39204 1.16895 7.77742 1.26754 8.12097 1.45565C8.46452 1.64376 8.75519 1.91533 8.96619 2.24532C9.35012 2.159 9.74965 2.17118 10.1276 2.28074C10.5056 2.39029 10.8497 2.59365 11.1279 2.87191C11.4062 3.15017 11.6096 3.49428 11.7191 3.87224C11.8287 4.2502 11.8408 4.64972 11.7545 5.03365C12.0845 5.24465 12.3561 5.53532 12.5442 5.87887C12.7323 6.22243 12.8309 6.60781 12.8309 6.99949C12.8309 7.39117 12.7323 7.77655 12.5442 8.1201C12.3561 8.46365 12.0845 8.75432 11.7545 8.96532C11.8405 9.34867 11.8282 9.74751 11.7189 10.1249C11.6097 10.5022 11.4069 10.8459 11.1294 11.124C10.8518 11.4021 10.5086 11.6056 10.1315 11.7156C9.75436 11.8257 9.35555 11.8388 8.97202 11.7537C8.7613 12.0849 8.4704 12.3576 8.12626 12.5466C7.78212 12.7355 7.39587 12.8346 7.00327 12.8346C6.61067 12.8346 6.22442 12.7355 5.88028 12.5466C5.53614 12.3576 5.24525 12.0849 5.03452 11.7537C4.65118 11.8396 4.25233 11.8274 3.87498 11.7181C3.49762 11.6088 3.15397 11.406 2.87588 11.1285C2.59779 10.851 2.39428 10.5077 2.2842 10.1306C2.17412 9.7535 2.16105 9.35468 2.24619 8.97115C1.91366 8.76071 1.63976 8.46959 1.44997 8.12486C1.26017 7.78014 1.16064 7.39301 1.16064 6.99949C1.16064 6.60597 1.26017 6.21884 1.44997 5.87411C1.63976 5.52939 1.91366 5.23826 2.24619 5.02782Z"
      stroke="#428DFF"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowUpRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M11.3328 11.3328V4.66718H4.66724M11.3328 4.66718L4.66724 11.3328"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

interface AuthorProps {
  avatar: string;
  name: string;
  handle: string;
  verified?: boolean;
}

// 作者行：设计稿 PC #10899:3792 单行排布，H5 #10899:5011 改为
// 左侧（头像 + 昵称/handle 两行）与右侧平台图标 space-between，图标顶对齐
const AuthorHeader: React.FC<AuthorProps> = ({ avatar, name, handle, verified }) => (
  <header className={styles.author}>
    <div className={styles.authorLeft}>
      <img className={styles.avatar} src={avatar} alt="" width={40} height={40} />
      <div className={styles.authorMeta}>
        <span className={styles.authorNameRow}>
          <span className={styles.authorName}>{name}</span>
          {verified && <BadgeCheckIcon />}
        </span>
        <span className={styles.authorHandle}>{handle}</span>
      </div>
    </div>
    <img className={styles.platform} src="/images/affiliate-program/x-logo.png" alt="X" width={20} height={20} />
  </header>
);

// 卡片内的真链接要盖过「整卡跳转」：不拦住冒泡的话，点 @Erasaprotect 会同时开两个标签页。
const stopCardNav = (e: React.MouseEvent) => e.stopPropagation();

// 真实创作者推荐（设计稿 #10899:3791 / #10899:3805）。
// 两条都是本人原帖，**任何界面语言下都保持原文** —— 一条英文、一条繁中，不走 i18n。
// 中文界面（zh / tw）把繁中那条排到前面，先让用户看到看得懂的一条；其余语言保持设计稿顺序。
const CREATOR_POSTS = {
  lizzlane: {
    key: 'lizzlane',
    avatar: '/images/affiliate-program/avatar-lizzlane.png',
    name: 'Lizz Lane',
    handle: '@lizzzlane',
    verified: true,
    href: CREATOR_POST_URLS.lizzlane,
    body: (
      <p className={styles.postText}>
        throwing this out there for creators who deal with leaks, I use{' '}
        <a
          className={styles.mention}
          href={ERASA_X_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stopCardNav}
        >
          @Erasaprotect
        </a>{' '}
        and they take everything down for me right away.
      </p>
    ),
  },
  waiteryoshie: {
    key: 'waiteryoshie',
    avatar: '/images/affiliate-program/avatar-waiteryoshie.png',
    name: 'YoShiE 冰塊 🧊',
    handle: '@waiteryoshie',
    verified: false,
    href: CREATOR_POST_URLS.waiteryoshie,
    // 设计稿 #10899:3809 定高 176，正文在「簡短來說」处截断并以 … 收尾，完整内容走 View Post。
    // lang="zh-Hant" 让读屏在英文界面下也用中文嗓音念这段，不会逐字母拼读。
    body: (
      <>
        <p className={styles.postText} lang="zh-Hant">
          這篇文是寫給
          <br />
          「有興趣保護自身權益的自媒體」
        </p>
        <p className={styles.postText} lang="zh-Hant">
          在上個月我和
          <br />
          <a
            className={styles.mention}
            href={ERASA_X_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopCardNav}
          >
            @Erasaprotect
          </a>
          <br />
          合作
        </p>
        <p className={styles.postText} lang="zh-Hant">
          簡短來說
          <br />…
        </p>
      </>
    ),
  },
};

/** How to Recommend Erasa：设计稿 #10899:3760 */
const HowToRecommend: React.FC<{ isModal?: boolean }> = ({ isModal = false }) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const SectionHeading = isModal ? 'h3' : 'h2';
  const CardHeading = isModal ? 'h4' : 'h3';

  // 简中与繁中都算中文
  const isChinese = router.locale === 'zh' || router.locale === 'tw';
  const creatorPosts = isChinese
    ? [CREATOR_POSTS.waiteryoshie, CREATOR_POSTS.lizzlane]
    : [CREATOR_POSTS.lizzlane, CREATOR_POSTS.waiteryoshie];

  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // 整卡跳原帖（需求文档「整张 Creator Card 可点击」）。
  // 键盘与读屏走卡片内既有的 View Post 链接 —— 所以这里**不加** role="link" / tabIndex，
  // 否则同一个目标会占两个 Tab 停留点、读屏也会把同一条帖子播报两遍。
  const onCardClick = (href: string) => {
    // 拖选正文后松开鼠标算一次 click，不该把人带走
    if (window.getSelection()?.toString()) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const onCopy = async (lines: string[], index: number) => {
    try {
      await navigator.clipboard.writeText(lines.map((line) => t(line)).join('\n\n'));
    } catch {
      // 剪贴板不可用（非安全上下文或用户拒绝授权）时静默失败，不切换到已复制态
      return;
    }
    ga_logEvent(EventName.affiliate_share_success, { position: 'post_ideas', index });
    setCopiedIndex(index);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <section className={styles.container}>
      {/* #10899:3762 */}
      <SectionHeading className={styles.title}>{t('How to Recommend Erasa')}</SectionHeading>

      {/* #10899:3763，背景为雪山图叠在 #428DFF 之上 */}
      <div className={styles.panel}>
        <div className={styles.content}>
          {/* #10899:3765 */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <CardHeading className={styles.cardTitle}>{t('Ready-to-Use Post Ideas')}</CardHeading>
            </div>

            {POST_IDEAS.map((lines, index) => (
              <article key={lines[0]} className={styles.post}>
                <div className={styles.postBody}>
                  {lines.map((line) => (
                    <p key={line} className={styles.postText}>
                      {t(line)}
                    </p>
                  ))}
                </div>
                <button
                  type="button"
                  className={`${styles.link} ${styles.copyLink}`}
                  onClick={() => onCopy(lines, index)}
                >
                  {copiedIndex === index ? <CheckIcon /> : <CopyIcon />}
                  {/* 两个标签叠在同一个网格单元里，宽度恒取两者较大值，
                      切换时按钮不会变宽变窄（也不依赖「哪个文案更长」的语言假设） */}
                  <span className={styles.copyLabel}>
                    <span>{copiedIndex === index ? t('Copied') : t('Copy Post')}</span>
                    <span className={styles.copyLabelSizer} aria-hidden="true">
                      {copiedIndex === index ? t('Copy Post') : t('Copied')}
                    </span>
                  </span>
                </button>
              </article>
            ))}

            {/* 读屏播报：仅复制成功时有内容；2 秒后清空为空串不会二次播报，
                因此视觉上的文案还原不会打扰读屏用户 */}
            <span className={styles.srOnly} role="status" aria-live="polite">
              {copiedIndex !== null ? t('Copied') : ''}
            </span>
          </div>

          {/* #10899:3787 */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <CardHeading className={styles.cardTitle}>{t('Real Creator Posts')}</CardHeading>
              <p className={styles.cardSubtitle}>{t('See What Erasa Users Are Saying')}</p>
            </div>

            {creatorPosts.map((post) => (
              <article
                key={post.key}
                className={`${styles.post} ${styles.postClickable}`}
                onClick={() => onCardClick(post.href)}
              >
                <AuthorHeader avatar={post.avatar} name={post.name} handle={post.handle} verified={post.verified} />
                {post.body}
                <a
                  className={styles.link}
                  href={post.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={stopCardNav}
                >
                  <span>{t('AffiliateViewPost')}</span>
                  <ArrowUpRightIcon />
                </a>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToRecommend;
